import { useEffect, useState } from 'react';

import { NavLink, useNavigate, useSearchParams } from 'react-router';

import { styled } from '@mui/material';

import { Button, Typography } from '@app-velocity/ui';

import { useRequestGateway } from '@app-velocity/request-gateway';

import { useI18n } from '@app-velocity/i18n';

import { Loader } from '@app-velocity/animations';

import { useCreateKeysMutation } from '../../queries/useCreateKeysMutation';
import { useRouterContext } from '../../router/useRouterContext';
import { useReadKeysQuery } from '../../queries/useReadKeysQuery';
import { useAuthProviderContext } from '../../context/useAuthProviderContext';

const Wrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
  textAlign: 'center',
  alignItems: 'center',
  justifyContent: 'center',
}));

export type AuthProviderProps = {
  type: 'figma' | 'google';
};

export const AuthProvider = ({ type }: AuthProviderProps) => {
  const { t } = useI18n();

  const { strategy } = useRouterContext();
  const { type: authType, setProviderType } = useAuthProviderContext();
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();
  const originalUrl = searchParams.get('originalUrl');
  const { mutate: createKeys, data: createdKeys } = useCreateKeysMutation();
  const [readKey, setReadKey] = useState('');
  const { writeTokens, baseUrl } = useRequestGateway();
  const { data: readKeyData, isError } = useReadKeysQuery(readKey);

  useEffect(() => {
    createKeys();
  }, []);

   
  useEffect(() => {
    if (!createdKeys?.readKey || !createdKeys.writeKey) return;

    const redirectUrl = `${baseUrl}/oauth2/authorization/${type}?writeKey=${createdKeys.writeKey}`;

    // it needs to be handled differently for webapp and figma plugin
    // webapp - store key in session/local storage and after returning - read the key from there and use to fetch tokens
    // figma plugin - redirect user to outside browser and store readKey locally, after user comes back to plugin - fetch data by using readKey

    if (strategy === 'WEB_APP') {
      if (authType === 'detached') {
        setReadKey(createdKeys.readKey);
      } else {
        window.sessionStorage.setItem('readKey', createdKeys.readKey);
        window.sessionStorage.setItem('originalUrl', originalUrl || '');
      }

      setTimeout(() => {
        if (authType === 'detached') {
          window.open(redirectUrl, '_blank');
        } else {
          window.location.replace(redirectUrl);
        }
      }, 500);
    } else if (strategy === 'FIGMA_PLUGIN') {
      setReadKey(createdKeys.readKey);
      setTimeout(() => {
        window.open(redirectUrl);
      }, 500);
    }
  }, [createdKeys]);

  // Used only in figma plugin
  useEffect(() => {
    if (!readKeyData) return;
    const { accessToken, refreshToken } = readKeyData;

    if (!accessToken || !refreshToken) return;

    const currentDate = new Date();
    const futureDate = currentDate.setMinutes(currentDate.getMinutes() + 10);

    writeTokens({
      accessToken,
      refreshToken,
      accessTokenExpiration: futureDate,
      refreshTokenExpiration: futureDate,
    });

    if (authType === 'default') {
      navigate('/');
    }
  }, [readKeyData]);

  // if query returns error that means that auth failed
  useEffect(() => {
    if (!isError) return;

    navigate('/');
  }, [isError]);

  return (
    <Wrapper>
      <Loader />
      {strategy === 'FIGMA_PLUGIN' ? (
        <Typography>{t('auth:browser_opened_plugin')}</Typography>
      ) : (
        <Typography>{t('auth:browser_opened_web')}</Typography>
      )}
      {authType === 'detached' ? (
        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={() => setProviderType(null)}
        >
          {t('shared:go_back')}
        </Button>
      ) : (
        <Button
          component={NavLink}
          to="/"
          size="small"
          variant="contained"
          color="primary"
        >
          {t('shared:go_back')}
        </Button>
      )}
    </Wrapper>
  );
};
